import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import { X, ArrowRight, ArrowLeft, Compass, CheckCircle2 } from "lucide-react";

const KEY = "kleeng_tour_done_v1";

const STEPS = [
  { sel: '[data-testid="nav-console"]', route: "/console", title: "Operator Console", body: "This is your mission control — treasury, revenue, spend and a live feed of every agent action." },
  { sel: '[data-testid="nav-agents"]', route: "/agents", title: "Agents Fleet", body: "Create wallet-backed AI agents here. Give each a mission and a budget; they earn and deliver for you." },
  { sel: '[data-testid="nav-treasury"]', route: "/treasury", title: "Treasury & Guardrails", body: "Fund the shared treasury and set spend caps + approval thresholds. Money only moves within your rules." },
  { sel: '[data-testid="nav-lead-sources"]', route: "/lead-sources", title: "Lead Sources", body: "The top of your funnel — discover local businesses via Google Places and win freelance gigs automatically." },
  { sel: '[data-testid="nav-outreach"]', route: "/outreach", title: "Local Outreach", body: "Auto-generate demo sites and run timed cold-outreach sequences. Replies become qualified leads." },
  { sel: '[data-testid="nav-deployments"]', route: "/deployments", title: "Deployment Pipeline", body: "Agents build & ship real sites here. You approve each production deploy; cost is charged only when it ships." },
  { sel: '[data-testid="nav-launch"]', route: "/launch", title: "Launch Readiness", body: "Track every go-live check in one place. Green the whole list to start earning real revenue." },
  { sel: '[data-testid="nav-guide"]', route: "/guide", title: "Guide & Tutorial", body: "Come back here any time for the full video walkthrough and downloadable handbook. That's the tour!" },
];

export default function ProductTour() {
  const navigate = useNavigate();
  const [active, setActive] = useState(false);
  const [i, setI] = useState(0);
  const [rect, setRect] = useState(null);

  const start = useCallback(() => { setI(0); setActive(true); }, []);
  const finish = useCallback(() => {
    setActive(false);
    localStorage.setItem(KEY, "1");
  }, []);

  // Auto-start once for brand-new operators; also allow manual start via event.
  useEffect(() => {
    const onStart = () => start();
    window.addEventListener("kleeng:start-tour", onStart);
    if (!localStorage.getItem(KEY)) {
      const t = setTimeout(() => start(), 1200);
      return () => { clearTimeout(t); window.removeEventListener("kleeng:start-tour", onStart); };
    }
    return () => window.removeEventListener("kleeng:start-tour", onStart);
  }, [start]);

  // Navigate to the step's route, then measure the highlighted nav element.
  useEffect(() => {
    if (!active) return;
    const step = STEPS[i];
    navigate(step.route);
    let tries = 0;
    const measure = () => {
      const el = document.querySelector(step.sel);
      if (el) {
        el.scrollIntoView({ block: "center", behavior: "smooth" });
        const r = el.getBoundingClientRect();
        setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
      } else if (tries++ < 20) {
        setTimeout(measure, 80);
      }
    };
    const t = setTimeout(measure, 120);
    return () => clearTimeout(t);
  }, [active, i, navigate]);

  useEffect(() => {
    if (!active) return;
    const onResize = () => {
      const el = document.querySelector(STEPS[i].sel);
      if (el) { const r = el.getBoundingClientRect(); setRect({ top: r.top, left: r.left, width: r.width, height: r.height }); }
    };
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onResize, true);
    return () => { window.removeEventListener("resize", onResize); window.removeEventListener("scroll", onResize, true); };
  }, [active, i]);

  if (!active || !rect) return null;

  const step = STEPS[i];
  const isLast = i === STEPS.length - 1;
  const pad = 6;
  const tipTop = Math.min(Math.max(rect.top - 10, 16), window.innerHeight - 220);
  const tipLeft = Math.min(rect.left + rect.width + 16, window.innerWidth - 340);

  return createPortal(
    <div className="fixed inset-0 z-[9999]" data-testid="product-tour" style={{ pointerEvents: "auto" }}>
      {/* dim + spotlight hole */}
      <div className="absolute rounded-lg transition-all duration-300"
        style={{
          top: rect.top - pad, left: rect.left - pad, width: rect.width + pad * 2, height: rect.height + pad * 2,
          boxShadow: "0 0 0 9999px rgba(4,7,12,0.78)", border: "1.5px solid #22D3EE", borderRadius: 10,
        }} />
      {/* tooltip */}
      <div className="absolute w-[320px] rounded-xl border border-cyan-500/40 bg-[#0D121C] p-4 shadow-2xl"
        data-testid="tour-tooltip" style={{ top: tipTop, left: tipLeft }}>
        <div className="flex items-center justify-between mb-1.5">
          <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider text-cyan-400">
            <Compass className="w-3.5 h-3.5" /> Tour · {i + 1}/{STEPS.length}
          </div>
          <button data-testid="tour-skip" onClick={finish} className="text-slate-500 hover:text-slate-300"><X className="w-4 h-4" /></button>
        </div>
        <h3 className="font-display font-bold text-[15px] text-slate-100">{step.title}</h3>
        <p className="text-[12.5px] text-slate-400 leading-relaxed mt-1.5">{step.body}</p>
        <div className="mt-3 flex items-center justify-between">
          <button data-testid="tour-back" onClick={() => setI((v) => Math.max(0, v - 1))} disabled={i === 0}
            className="flex items-center gap-1 text-[11px] font-mono uppercase tracking-wider text-slate-400 disabled:opacity-30 hover:text-slate-200">
            <ArrowLeft className="w-3.5 h-3.5" /> Back
          </button>
          <div className="flex gap-1">
            {STEPS.map((_, k) => <span key={k} className={`w-1.5 h-1.5 rounded-full ${k === i ? "bg-cyan-400" : "bg-slate-700"}`} />)}
          </div>
          {isLast ? (
            <button data-testid="tour-finish" onClick={finish}
              className="flex items-center gap-1.5 rounded-md bg-emerald-500 text-[#04120C] px-3 py-1.5 font-mono text-[11px] uppercase tracking-wider font-bold hover:bg-emerald-400 transition-colors">
              <CheckCircle2 className="w-3.5 h-3.5" /> Done
            </button>
          ) : (
            <button data-testid="tour-next" onClick={() => setI((v) => Math.min(STEPS.length - 1, v + 1))}
              className="flex items-center gap-1.5 rounded-md bg-cyan-500 text-[#04121A] px-3 py-1.5 font-mono text-[11px] uppercase tracking-wider font-bold hover:bg-cyan-400 transition-colors">
              Next <ArrowRight className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
